import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ConfirmDialog, ErrorNote } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { apiAssetUrl, company } from '../../lib/resources';
import { DEFAULT_THEME, FONT_OPTIONS, loadFont, themeVars } from '../../lib/theme';
import type { WorkspaceTheme } from '../../types';

const COLORS: Array<[keyof WorkspaceTheme, string, string]> = [
  ['primary', 'Cor principal', 'Botões, item ativo do menu, destaques'],
  ['primaryForeground', 'Texto sobre a cor principal', 'Precisa contrastar com a principal'],
  ['accent', 'Destaque', 'Detalhes e gráficos'],
  ['sidebar', 'Fundo do menu lateral', ''],
  ['sidebarForeground', 'Texto do menu lateral', ''],
];

/** Contraste WCAG entre duas cores hex (1 a 21). */
function contrast(a: string, b: string): number {
  const luminance = (hex: string) => {
    const value = hex.replace('#', '');
    const [r, g, bl] = [0, 2, 4].map((index) => {
      const channel = parseInt(value.slice(index, index + 2), 16) / 255;
      return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

const fromSession = (theme: WorkspaceTheme | undefined): Required<WorkspaceTheme> => ({
  ...DEFAULT_THEME,
  ...Object.fromEntries(Object.entries(theme ?? {}).filter(([, value]) => Boolean(value))),
  fontFamily:
    theme?.fontFamily && (FONT_OPTIONS as readonly string[]).includes(theme.fontFamily)
      ? theme.fontFamily
      : DEFAULT_THEME.fontFamily,
});

/** Configurações › Aparência (Fase 5 / P2): cores, fonte e logo do workspace. */
export function AppearanceSection() {
  const { session, refreshSession } = useAuth();
  const saved = fromSession(session?.workspace.theme);
  const [theme, setTheme] = useState<Required<WorkspaceTheme>>(saved);
  const [resetting, setResetting] = useState(false);
  const dirty = JSON.stringify(theme) !== JSON.stringify(saved);

  useEffect(() => loadFont(theme.fontFamily), [theme.fontFamily]);

  const save = useMutation({
    mutationFn: (next: WorkspaceTheme) => company.updateTheme(next),
    onSuccess: async () => {
      await refreshSession();
      setResetting(false);
    },
  });

  const lowContrast =
    contrast(theme.primary, theme.primaryForeground) < 3 ||
    contrast(theme.sidebar, theme.sidebarForeground) < 3;
  const logo = apiAssetUrl(session?.workspace.logoUrl);

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">Aparência</h2>
        <p className="text-xs text-slate-500">
          Cores, fonte e logo deste workspace. Vale para todos os membros.
        </p>
      </header>

      <div className="flex flex-wrap items-start gap-6 p-5">
        <div className="min-w-[260px] flex-1 space-y-4">
          {COLORS.map(([key, label, hint]) => (
            <label key={key} className="flex items-center gap-3 text-sm">
              <input
                type="color"
                className="h-9 w-14 shrink-0 cursor-pointer rounded border border-slate-200"
                value={theme[key]}
                onChange={(event) =>
                  setTheme((current) => ({
                    ...current,
                    [key]: event.target.value.toUpperCase(),
                  }))
                }
              />
              <span>
                <span className="font-medium text-slate-700">{label}</span>
                <span className="ml-2 font-mono text-xs text-slate-400">{theme[key]}</span>
                {hint && <span className="block text-xs text-slate-400">{hint}</span>}
              </span>
            </label>
          ))}
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Fonte</span>
            <select
              className="mt-1 block w-64 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={theme.fontFamily}
              onChange={(event) =>
                setTheme((current) => ({ ...current, fontFamily: event.target.value }))
              }
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font} value={font}>
                  {font}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm">
            <span className="font-medium text-slate-700">Logo</span>
            <p className="text-xs text-slate-500">
              O logo é o da empresa —{' '}
              <Link to="/configuracoes/empresa" className="underline">
                troque em Configurações › Empresa
              </Link>
              .
            </p>
          </div>
          {lowContrast && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              Pouco contraste entre texto e fundo — pode ficar difícil de ler.
            </p>
          )}
          {save.isError && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(theme)}
            >
              {save.isPending ? 'Salvando…' : 'Salvar aparência'}
            </Button>
            <Button disabled={!dirty} onClick={() => setTheme(saved)}>
              Desfazer
            </Button>
            <Button variant="ghost" onClick={() => setResetting(true)}>
              Restaurar padrão
            </Button>
          </div>
        </div>

        {/* Prévia ao vivo, só nesta caixa */}
        <div
          className="w-full max-w-sm shrink-0 overflow-hidden rounded-xl border border-slate-200 shadow-sm"
          style={{ ...themeVars(theme), fontFamily: 'var(--crm-font)' } as React.CSSProperties}
          aria-label="Prévia da aparência"
        >
          <div className="flex h-64">
            <div
              className="w-28 space-y-1 p-2 text-[11px]"
              style={{
                backgroundColor: 'var(--crm-sidebar)',
                color: 'var(--crm-sidebar-foreground)',
              }}
            >
              {logo ? (
                <img src={logo} alt="" className="mb-2 h-6 max-w-full object-contain" />
              ) : (
                <p className="mb-2 truncate font-semibold">{session?.workspace.name}</p>
              )}
              <p
                className="rounded px-2 py-1"
                style={{
                  backgroundColor: 'var(--crm-primary)',
                  color: 'var(--crm-primary-foreground)',
                }}
              >
                Início
              </p>
              <p className="px-2 py-1 opacity-80">Pipelines</p>
              <p className="px-2 py-1 opacity-80">Leads</p>
            </div>
            <div className="flex-1 space-y-3 bg-slate-50 p-3">
              <p className="text-sm font-semibold text-slate-900">Negócios do mês</p>
              <div className="h-2 rounded-full bg-slate-200">
                <div
                  className="h-2 w-2/3 rounded-full"
                  style={{ backgroundColor: 'var(--crm-accent)' }}
                />
              </div>
              <button
                type="button"
                className="rounded-lg px-3 py-1.5 text-xs font-medium"
                style={{
                  backgroundColor: 'var(--crm-primary)',
                  color: 'var(--crm-primary-foreground)',
                }}
              >
                + Novo negócio
              </button>
              <p className="text-xs text-slate-500">
                O texto do sistema usa a fonte escolhida.
              </p>
            </div>
          </div>
        </div>
      </div>

      {resetting && (
        <ConfirmDialog
          title="Restaurar a aparência padrão?"
          message="Cores e fonte voltam ao padrão do CRM para todos os membros. O logo não muda."
          confirmLabel="Restaurar"
          busy={save.isPending}
          error={save.isError ? errorMessage(save.error) : null}
          onConfirm={() => {
            setTheme(DEFAULT_THEME);
            save.mutate({});
          }}
          onClose={() => setResetting(false)}
        />
      )}
    </section>
  );
}
